import { useCallback, useMemo, useState } from 'react';
import type { SongPublishDefaultsResponse, SongPublishSharing } from 'fym-shared/src/music24/songPublish';
import { useCreateSongPublishMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useArtistTimeZone } from '../../../settings/tabs/artist/timeZone/useArtistTimeZone';
import { localReleaseStart, publishAtOfValue, PublishTimingValue } from '../../promote/publish/PublishTimingField';
import {
  defaultSongUploadTiming,
  isLaterTooSoon,
  SongUploadError,
  songUploadErrorOf,
  songUploadTimings,
} from '../songUpload';

/**
 * The state of the upload form (design c3, c4) and its POST to `/accounts/:accountId/song-publishes`.
 * The time zone of "On release day" is the artist time zone, else the browser zone.
 */
export function useSongUploadForm({
  accountId,
  defaults,
  onDone,
}: {
  accountId: number;
  defaults: SongPublishDefaultsResponse;
  onDone: () => void;
}) {
  const timeZone = useArtistTimeZone();
  const now = useMemo(() => new Date(), []);
  const { releaseDate } = defaults.facts;
  const releaseStart = useMemo(() => localReleaseStart(releaseDate, timeZone), [releaseDate, timeZone]);
  const timings = useMemo(() => songUploadTimings(releaseStart, now), [now, releaseStart]);
  const [title, setTitle] = useState(defaults.title);
  const [description, setDescription] = useState(defaults.description);
  const [sharing, setSharing] = useState<SongPublishSharing>(defaults.sharing);
  const [timing, setTiming] = useState<PublishTimingValue>({
    timing: defaultSongUploadTiming(releaseStart, now),
    pickedAt: '',
  });
  const [localError, setLocalError] = useState<SongUploadError | null>(null);
  const [create, createState] = useCreateSongPublishMutation();
  const publishAt = timing.timing === 'now' ? null : publishAtOfValue(timing, releaseDate, timeZone);

  const changeTiming = useCallback((value: PublishTimingValue) => {
    setLocalError(null);
    setTiming(value);
  }, []);

  const submit = useCallback(async () => {
    if (timing.timing === 'later' && isLaterTooSoon(publishAt, new Date())) {
      setLocalError('tooSoon');
      return;
    }
    setLocalError(null);
    const result = await create({
      accountId,
      body: {
        catalogItemUuid: defaults.catalogItemUuid,
        channel: defaults.channel,
        timing: timing.timing,
        publishAt,
        title: title.trim(),
        description,
        sharing,
      },
    });
    if (isSuccessfulResult(result)) {
      onDone();
    }
  }, [accountId, create, defaults, description, onDone, publishAt, sharing, timing.timing, title]);

  let error: SongUploadError | null = localError;
  if (error === null && createState.error !== undefined) {
    error = songUploadErrorOf(createState.error);
  }

  return {
    title,
    setTitle,
    description,
    setDescription,
    sharing,
    setSharing,
    timing,
    changeTiming,
    timings,
    publishAt,
    submit,
    isSubmitting: createState.isLoading,
    error,
  };
}

export type SongUploadFormState = ReturnType<typeof useSongUploadForm>;
